import React, { PureComponent, Fragment } from 'react';
import PropTypes from 'prop-types';
import {
  get,
  set,
  startsWith,
  cloneDeep,
  isEmpty,
  isNil,
  isNumber,
  isFunction,
  merge,
  pick,
  omit,
  omitBy,
  zipObject,
  partition,
  toPairs,
  negate,
  toLower,
  includes,
} from 'lodash';
import { Modal, Input, Radio, AutoComplete, Icon, Popover } from 'antd';
import {
  Form as LegacyForm,
  Icon as LegacyIcon,
} from '@ant-design/compatible';
import { default as WorkOperationsTableComp } from '../WorkOperationsTable';
import FixterAutocomplete from '../Autocomplete';
import SimpleStepper from '../Stepper';
import ActionButtons from './ActionButtons';
import Wrapper, { AddPartButton, TextArea, QeTag, RBContainer, CommentContainer } from './styled';
import DeleteRowButton from './DeleteRowButton';

const WorkOperationTableInstanceSymbol = Symbol('WorkOperationTableInstance');
const { Option } = AutoComplete;

export const ROW_TYPE = Object.freeze({
  labour: 'labour',
  part: 'part',
});

export default class WorkOperationEditor extends PureComponent {
  refWorkOperationTable = React.createRef();

  [WorkOperationTableInstanceSymbol] = null;

  isNew = !get(this.props.workOperation, 'key');

  fieldOptions = {
    urgency: toPairs(this.props.fieldOptions.urgency.list),
    workAutocomplete: omit(this.props.fieldOptions.workAutocomplete, ['onSelect']),
    workDescription: omit(this.props.fieldOptions.workDescription, ['onChange']),
    commentForCM: omit(this.props.fieldOptions.commentForCM, ['onChange']),
    commentForCustomer: omit(this.props.fieldOptions.commentForCustomer, ['onChange']),
  };

  showAutocomplete = this.isNew && !isEmpty(this.props.autocompleteList);

  constructor(props) {
    super(props);
    const { urgency, workSummary = '', children = [], comments = [], type, sku } = props.workOperation;

    this.state = {
      sku,
      type,
      step: !this.isNew ? 4 : 1,
      /**
       * Holds modified data of existing line items
       */
      overrideLineItems: [],
      /**
       * Holds newly added line items
       * key:
       * * `new-*` added with data
       * * `new-row-*` new row to be filled with data
       */
      newLineItems: cloneDeep(children.filter(this.isNewLineItem)),
      removeLineItems: [],
      /**
       * Holds modified data of total row
       */
      overrideTotalRow: {},
      urgency,
      workSummary,
      commentForCM: comments.filter(c =>
        !c.type || c.type === 'garage-comment'
      ).map(({ message }) => message)[0] || '',
      commentForCustomer: comments.filter(c =>
        c.type === 'cm-comment'
      ).map(({ message }) => message)[0] || '',
      lastOrderNo: children.length + 1,
      showWorkDescription: !this.showAutocomplete,
      saveDisabled: !workSummary,
      validateStatusWorkSummary:'success',
      validateStatusWorkDescription:'success',
      validateStatusCmComment:'success',
      validateStatusCustomerComment:'success',
    };
  }

  get dataSource() {
    const { workSummary } = this.state;
    const { hideAddPartRow } = this.props;

    const children = [...this.lineItems];

    if (!hideAddPartRow) children.push(this.addPartRow);

    return [
      {
        key: 'title',
        workSummary,
        editable: false,
        children,
      },
      this.totalRow,
    ];
  }

  get lineItems() {
    const { newLineItems, removeLineItems } = this.state;
    const lineItems = this.lineItemsWithOverride;
    // set newly added rows
    lineItems.push(...newLineItems);
    return lineItems.filter(({ key }) => !removeLineItems.includes(key));
  }

  get lineItemsWithOverride() {
    const { overrideLineItems } = this.state;
    const lineItems = (cloneDeep(this.props.workOperation.children) || []).filter(negate(this.isNewLineItem));
    // set modified data
    overrideLineItems.forEach(oli => {
      const li = lineItems.find(({ key }) => key === oli.key);
      if (!li) return;
      merge(li, oli);
    });
    return lineItems.map(li =>
      this.renderLineItemDeleteButton(Object.assign(li, { parentId: this.props.workOperation.key }))
    );
  }

  get addPartRow() {
    return {
      key: 'add-part-row',
      render: {
        workSummary: () => (
          <AddPartButton id="T_WoEdit_AddPart" onClick={this.handleAddPart} disabled={this.isEmptyNewRow} />
        ),
      },
      editable: false,
    };
  }

  get totalRow() {
    const { workOperation, fixedTotalColumns, editTotalRow } = this.props;
    const { overrideTotalRow } = this.state;
    // Child sums win so deleting a sub-line updates the customer total on display and save.
    // Quote-engine prices are kept on the children in prepareSelectedWorkOperationPricing.
    return Object.assign(
      zipObject(fixedTotalColumns, Array(fixedTotalColumns.length).fill(0)),
      pick(workOperation, fixedTotalColumns),
      overrideTotalRow,
      this.calculateTotals(),
      {
        key: 'total',
        editable: editTotalRow,
      }
    );
  }

  get isEmptyNewRow() {
    const newRow = this.state.newLineItems.find(({ key }) => startsWith(key, 'new-row'));
    if (!newRow) return false;
    return newRow.workSummary === '';
  }

  get WorkOperationTableInstance() {
    if (this[WorkOperationTableInstanceSymbol]) {
      return Promise.resolve(this[WorkOperationTableInstanceSymbol]);
    }
    return new Promise(resolve => {
      const tableMount = setInterval(() => {
        // try until table is mounted
        this[WorkOperationTableInstanceSymbol] = this.refWorkOperationTable.current;
        if (this[WorkOperationTableInstanceSymbol]) {
          clearInterval(tableMount);
          resolve(this[WorkOperationTableInstanceSymbol]);
        }
      }, 100);
    });
  }

  isNewLineItem = ({ key }) => startsWith(key, 'new-');

  makeLabourLineItem = extend =>
    this.makeNewLineItem(
      Object.assign(
        {
          workSummary: 'Labour',
          key: `new-${ROW_TYPE.labour}`,
          type: ROW_TYPE.labour,
          className: ROW_TYPE.labour,
          order: 1, // always first item
        },
        extend
      )
    );

  /**
   * Use to create a line item with the data structure of where the component is used
   */
  makeNewLineItem = extend => {
    const { defaultLineItemData, workOperation } = this.props;
    const { vatExempt = false, key } = workOperation;
    const order = get(this.state, 'lastOrderNo', 0) + 1;
    const lineItem = Object.assign(
      {
        key: `new-row-${order}`, // editable new row
        parentId: key,
        workSummary: '',
        vatExempt,
        order,
        className: 'new-row',
      },
      defaultLineItemData,
      extend
    );
    return this.renderLineItemDeleteButton(lineItem);
  };

  /**
   * Use to add new line items from outside the component (ref)
   * Eg. add new line items when select from autocomplete
   */
  setNewLineItems = (lineItems = []) => {
    let lastOrderNo = 0;
    const newLineItems = lineItems.map(li => {
      lastOrderNo += 1;
      if (li.type === ROW_TYPE.labour) return this.makeLabourLineItem(li);
      const nli = this.makeNewLineItem({ ...li, key: `new-${lastOrderNo}` });
      return nli;
    }).map(li => {
      li.render = li.render || {};

      if (li.generatedByQuoteEngine || li.providedBy) {
        if (this.props.displayQuoteEnginetooltip) {
          li.render.workSummary = ({ defaultRender }) => (
            <Fragment>
              <span style={{ marginRight: 5 }}>{defaultRender}</span>
              <QeTag>
                <LegacyIcon type="check-circle" theme="filled" />
                Provided by { li.providedBy ? li.providedBy : 'Quote Engine' }
              </QeTag>
            </Fragment>
          );
        }
      }
      li.className += ' hightlight';
      ['quantity', 'price', 'garageCost', 'garageCostWithVat', 'fixedCostWithVAT'].forEach(fieldName => {
        li.render[fieldName] = ({ defaultRender, value }) => (
          <span className={!value ? 'hightlightWrapper' : ''}>{defaultRender}</span>
        );
      });

      return li;
    });
    this.setState({
      newLineItems,
      overrideLineItems: [],
      removeLineItems: [],
      lastOrderNo,
    });
  };

  /**
   * Returns an object with calculateTotalColumns keys and corresponding sum of all line items
   */
  calculateTotals = () => {
    const { calculateTotalColumns } = this.props;
    return this.lineItems.reduce((total, li) => {
      calculateTotalColumns.forEach(col => (isNumber(li[col]) ? (total[col] += li[col]) : null));
      return total;
    }, zipObject(calculateTotalColumns, Array(calculateTotalColumns.length).fill(0)));
  };

  onSetEditRow = editingKey => {
    if (!this.isEmptyNewRow) this.storeNewRowData();
    else if (!startsWith(editingKey, 'new-row')) this.removeEmptyNewRow();
    const { onSetEditRow } = this.props;
    onSetEditRow(editingKey);
  };

  getLineItemByKey = key => this.lineItems.find(li => li.key === key);

  getColumnByKey = columnKey => this.columns.find(col => col.key === columnKey);

  storeNewRowData = () => {
    const newRowPos = this.state.newLineItems.findIndex(({ key }) => startsWith(key, 'new-row'));
    if (newRowPos === -1) return;
    this.setState(state => {
      const newLineItems = [...state.newLineItems];
      const newRow = newLineItems[newRowPos];
      newLineItems.splice(newRowPos, 1, {
        ...newRow,
        key: newRow.key.replace('-row', ''),
      });
      return { newLineItems };
    });
  };

  removeEmptyNewRow = () => {
    const newRowPos = this.state.newLineItems.findIndex(({ key }) => startsWith(key, 'new-row'));
    if (newRowPos === -1) return;
    this.setState(state => {
      const newLineItems = [...state.newLineItems];
      newLineItems.splice(newRowPos, 1);
      return { newLineItems };
    });
  };

  handleUrgencyChange = e => {
    const urgency = e.target.value;
    const validateStatusCmComment = includes(['dangerous', 'major'], toLower(urgency)) ?
      'success' : this.state.validateStatusCmComment;

    this.setState({ urgency, validateStatusCmComment, step: 3 }, () => {
      const { onChange } = this.props.fieldOptions.urgency;
      if (isFunction(onChange)) onChange(urgency);
    });
  };

  handleWorkSelected = ({ predefinedId }) => {
    const { autocompleteList } = this.props;
    const op = autocompleteList.find(o => o.predefinedId === predefinedId);
    const isOther = get(op, 'type') === 'custom';
    const workSummary = !op || isOther ? '' : get(op, 'workSummary', '');
    this.setState(
      {
        showWorkDescription: isOther,
        urgency: null,
        workSummary,
        validateStatusWorkSummary: 'success',
        step: 2,
      },
      () => {
        const { onSelect } = this.props.fieldOptions.workAutocomplete;
        if (isFunction(onSelect)) onSelect(op);
      }
    );
  };

  getOptions = () => {
    const { autocompleteList } = this.props;
    const getColor = optionType => optionType === 'custom' ? '1px solid black' : 'none';
    return autocompleteList
      .map(opt => (
        <Option key={opt.predefinedId} value={opt.predefinedId} style={{ borderTop: getColor(opt.type) }}>
          {opt.text}
        </Option>
      ));
  };

  handleWorkSelectedOnBlur = selected => {
    const { autocompleteList } = this.props;
    const op = autocompleteList.find(o => o.predefinedId === selected);

    if (!op) {
      this.setState({
        showWorkDescription: false,
        workSummary: '',
        validateStatusWorkSummary: 'error',
      }, () => {
        const { onChange } = this.props.fieldOptions.workDescription;

        if (isFunction(onChange)) onChange('');
      });
    }
  };

  handleFilterAutoComplete = (inputValue, option) =>
    get(option, 'props.children', '')
      .toLowerCase()
      .includes(inputValue.toLowerCase()) || option.key === 'OTHER';

  handleWorkDescriptionChange = e => {
    const workSummary = e.target.value;
    const validateStatusWorkDescription = isEmpty(workSummary) ? 'error' : 'success';
    this.setState({ workSummary, validateStatusWorkDescription }, () => {
      const { onChange } = this.props.fieldOptions.workDescription;
      if (isFunction(onChange)) onChange(workSummary);
    });
  };

  handleWorkDescriptionOnBlur = () => {
    const { workSummary } = this.state;

    if (isEmpty(workSummary)) {
      this.setState({ validateStatusWorkDescription: 'error' });
    }
  };

  areCommentsNotValid = () => {
    const { urgency, commentForCM, commentForCustomer } = this.state;

    if (!this.props.isCommentMandatory) {
      return false;
    }

    if (this.fieldOptions.commentForCM.display && !this.fieldOptions.commentForCM.disabled) {
      const invalidCommentForCM = isEmpty(commentForCM) && !includes(['dangerous', 'major'], toLower(urgency));
      if (invalidCommentForCM) {
        return true;
      }
    }

    if (this.fieldOptions.commentForCustomer.display && !this.fieldOptions.commentForCustomer.disabled) {
      const invalidCommentForCcustomer = isEmpty(commentForCustomer);
      if (invalidCommentForCcustomer) {
        return true;
      }
    }

    return false;
  }

  isSaveButtonDisabled = () => {
    const { workSummary, newLineItems, overrideLineItems } = this.state;
    const hasIncompleteLines = [...overrideLineItems, ...newLineItems].find(
      l => isEmpty(l.workSummary) || isNil(l.price) || isNil(l.quantity)
    );
    const isWorkSummaryValid = !isEmpty(workSummary);
    const hasValidationErrors = typeof hasIncompleteLines === 'object' || !isWorkSummaryValid;
    return hasValidationErrors || this.areCommentsNotValid();
  };

  handleCMCommentChange = e => {
    const commentForCM = e.target.value;
    const validateStatusCmComment = (this.props.isCommentMandatory && isEmpty(commentForCM)) ? 'error' : 'success';
    this.setState({ commentForCM, validateStatusCmComment });
  };

  handleCustomerCommentChange = e => {
    const commentForCustomer = e.target.value;
    const validateStatusCustomerComment = (this.props.isCommentMandatory && isEmpty(commentForCustomer)) ? 'error' : 'success';
    this.setState({ commentForCustomer, validateStatusCustomerComment });
  };

  handleCMCommentOnBlur = () => {
    const { commentForCM, urgency } = this.state;

    if (this.props.isCommentMandatory && !includes(['dangerous', 'major'], toLower(urgency)) && isEmpty(commentForCM)) {
      this.setState({ validateStatusCmComment: 'error' });
    }
  };

  handleCustomerCommentOnBlur = () => {
    const { commentForCustomer } = this.state;

    if (this.props.isCommentMandatory && isEmpty(commentForCustomer)) {
      this.setState({ validateStatusCustomerComment: 'error' });
    }
  };

  handleAddPart = () => {
    this.WorkOperationTableInstance.then(inst => {
      inst.setEditingKey(null);
      this.storeNewRowData();
      const newLineItem = this.makeNewLineItem({
        type: ROW_TYPE.part,
        className: ROW_TYPE.part,
      });
      this.setState(({ newLineItems, lastOrderNo }) => ({
        newLineItems: [...newLineItems, newLineItem],
        lastOrderNo: lastOrderNo + 1,
      }));
    });
  };

  handleRemoveLineItem = key =>
    this.setState(({ newLineItems, removeLineItems }) => {
      this.props.onLineItemDelete(key);
      if (startsWith(key, 'new-')) {
        return {
          newLineItems: newLineItems.filter(nli => nli.key !== key),
        };
      }
      return {
        removeLineItems: [...removeLineItems, key],
      };
    });

  handleDelete = () => {
    const { workOperation, onDelete } = this.props;
    onDelete(workOperation.key);
  };

  handleSave = () => {
    const { workOperation, onSave } = this.props;
    const { urgency, workSummary, commentForCM, commentForCustomer, newLineItems, removeLineItems } = this.state;
    const wo = omitBy(Object.assign({ vatExempt: false }, workOperation, { workSummary }), isNil);
    const lineItems = this.lineItemsWithOverride
      .filter(({ key }) => !removeLineItems.includes(key))
      .concat(newLineItems)
      .map(li => omit(li, ['className', 'render']));

    const updatedWo = {
      ...omit(wo, ['className', 'render']),
      ...omit(this.totalRow, ['key', 'editable']),
      urgency,
      lineItems,
      deleted: removeLineItems,
    };

    if (this.fieldOptions.commentForCM.display && !this.fieldOptions.commentForCM.disabled) {
      updatedWo.comments = [
        { message: commentForCM || '', type: 'garage-comment', updatedAt: Date.now() },
        ...(updatedWo.comments || []).filter(
          c => c.type && c.type !== 'garage-comment',
        ),
      ];
    }

    if (this.fieldOptions.commentForCustomer.display && !this.fieldOptions.commentForCustomer.disabled) {
      updatedWo.comments = [
        { message: commentForCustomer || '', type: 'cm-comment', updatedAt: Date.now() },
        ...(updatedWo.comments || []).filter(
          c => c.type !== 'cm-comment',
        ),
      ];
    }

    onSave(updatedWo);
  };

  /**
   * Make sure `column.input.onChange` returns a lineItem
   */
  handleInputChange = (onChange, onAfterChange) => (e, { columnKey, form, recordKey }) => {
    const val = get(e, 'target.value', e);
    const isNewRow = startsWith(recordKey, 'new');
    const record = this.getLineItemByKey(recordKey);
    let lineItem = {
      ...(recordKey === 'total' ? this.totalRow : record),
      ...form.getFieldsValue(),
      [columnKey]: val,
    };
    if (isFunction(onChange)) lineItem = onChange(lineItem, columnKey);
    this.setState(
      ({ overrideLineItems, newLineItems }) => {
        if (recordKey === 'total') return { overrideTotalRow: lineItem };
        const stateLineItems = cloneDeep(isNewRow ? newLineItems : overrideLineItems);
        const liPos = stateLineItems.findIndex(({ key }) => key === recordKey);
        if (liPos > -1) stateLineItems.splice(liPos, 1, lineItem);
        else stateLineItems.push(lineItem);
        return isNewRow ? { newLineItems: stateLineItems } : { overrideLineItems: stateLineItems };
      },
      () =>
        isFunction(onAfterChange)
          ? onAfterChange(this.state, this.totalRow, lineItem, { columnKey, form })
          : null
    );
  };

  handleInputOnBlur = (onBlur) => (e, { columnKey, form, recordKey }) => {
    const val = get(e, 'target.value', e);
    const isWorkSummaryValid = !isEmpty(val);

    if (columnKey === 'workSummary' && !isWorkSummaryValid) {
      form.validateFields();
      if (isFunction(onBlur)) onBlur({ columnKey, form, recordKey });
    }
  };

  /**
   * onChange(lineItem, columnKey) from column input will run when changing data on column input
   * and return the lineItem with modified data
   */
  setHandleInputChange = column => {
    if (!isEmpty(column.children)) column.children.forEach(this.setHandleInputChange);
    if (column.input && isFunction(column.input.onChange)) {
      column.input.onChange = this.handleInputChange(
        column.input.onChange,
        get(column.input, 'onAfterChange')
      );
    } else set(column, 'input.onChange', this.handleInputChange(li => li));
    if (column.input && column.key === 'workSummary' && isFunction(column.input.onBlur)) {
      column.input.onBlur = this.handleInputOnBlur(column.input.onBlur);
    } else if (column.key === 'workSummary') set(column, 'input.onBlur', this.handleInputOnBlur(li => li));
    return column;
  };

  renderLineItemDeleteButton = lineItem => {
    lineItem.render = lineItem.render || {};
    lineItem.render.delete = ({ recordKey }) => {
      const { type } = this.getLineItemByKey(recordKey);
      if (type === ROW_TYPE.labour) return null;
      return (
        <DeleteRowButton
          id="T_WoEdit_DeleteLine"
          onClick={e => {
            e.stopPropagation();
            this.handleRemoveLineItem(recordKey);
          }}
        />
      );
    };
    return lineItem;
  };

  // eslint-disable-next-line react/sort-comp
  columns = cloneDeep(this.props.columns).map(this.setHandleInputChange);

  commentPlaceholder = () => this.props.isCommentMandatory && !this.fieldOptions.commentForCM.disabled ?
    'Please explain why the work operation is needed, eg: brake pads thickness is 1.6mm (legal minimum: 1.9mm)' : '';

  commentForCustomerPlaceholder = () => this.props.isCommentMandatory ?
    'Please explain why the work operation need to be carried out.' : '';

  getSuggestions = () => {
    const { autocompleteList } = this.props;
    const sortByAz = (a, b) => a.text.localeCompare(b.text);

    const [
      motAndServiceList,
      othersList,
    ] = partition(autocompleteList.sort(sortByAz), (item) => item.type === 'mot' || item.type === 'service');
    const [
      diagnosticsList,
      otherList,
    ] = partition(othersList, (item) => item.category === 'Car diagnostic');

    const [
      customList,
      repairsList,
    ] = partition(otherList, ({ type }) => type === 'custom');

    return [
      {
        title: 'Repairs',
        products: repairsList,
      },
      {
        title: 'MOT & Servicing',
        products: motAndServiceList,
      },
      {
        title: 'Diagnostics',
        products: diagnosticsList,
      },
      {
        products: customList,
        custom: true,
      },
    ];
  }

  getSuggestionValue = item => item.text;

  renderSuggestionValue = item => {
    const { searched } = this.state;
    const { text } = item;

    const i = text.toLowerCase().indexOf(searched);
    if (!searched || i === -1) {
      return text;
    }

    const bold1 = text.substr(0, i);
    const normal = text.substr(i, searched.length);
    const bold2 = text.substr(i + searched.length, text.length - 1);

    return (
      <Fragment>
        <b>{bold1}</b>
        {normal}
        <b>{bold2}</b>
      </Fragment>
    );
  }

  renderSectionTitle = (section) => {
    if (section.custom) {
      return <div style={{ borderTop: 'solid 1px #dee6f0', padding: 0 }} />;
    }
    return (<strong>{section.title.toUpperCase()}</strong>);
  }

  getSectionSuggestions = (section) => section.products

  filterSuggestions = (items, value) => {
    const { filterOption } = this.props.fieldOptions.workAutocomplete;
    this.setState({ searched: toLower(value) });

    if (filterOption) {
      const results = filterOption(items, value);
      return results.filter(i => i.products.length > 0);
    }

    return items.map(i => ({
      ...i,
      products: i.products.filter(item => {
        if (i.custom) { // always display other;
          return true;
        }

        return (
          toLower(item.text).includes(toLower(value))
          || (item.searchTerms || '')
            .split(',')
            .map(s => s.trim().toLowerCase())
            .filter(s => s.length > 0)
            .reduce(((accumulator, currentValue) =>
              accumulator || currentValue === toLower(value)
            ), false)
        );
      }),
    })).filter(i => i.products.length > 0);
  }

  handleReset = () => {
    this.setState({
      urgency: null,
      workSummary: null,
      commentForCM: null,
      showWorkDescription: null,
      step: 1,
    });
  }

  render() {
    const { validator, onClose } = this.props;
    const { urgency, workSummary, commentForCM, commentForCustomer, showWorkDescription, validateStatusWorkSummary,
      validateStatusWorkDescription, validateStatusCmComment, validateStatusCustomerComment, step, sku } = this.state;

    return (
      <Modal
        onCancel={onClose}
        width={1000}
        footer={null}
        visible
        keyboard={false}
        bodyStyle={{ padding: 0 }}
      >
        <SimpleStepper
          loadedSteps={step}
          totalSteps={4}
          height={6}
          loadedColor="#0080f2"
          backgroundColor="#e8e8e8"
          style={{ borderRadius: '4px 4px 0 0' }}
          radiusTop={4}
        />
        <Wrapper>
          <Fragment>
            <div className="work-summary">
              {this.showAutocomplete && (
                <div>
                  <LegacyForm style={{ maxWidth: '75%' }}>
                    <h3>Add a work operation</h3>
                    <LegacyForm.Item
                      validateStatus={validateStatusWorkSummary}
                      help={validateStatusWorkSummary === 'error' ? 'Please select an option' : ''}
                    >
                      <FixterAutocomplete
                        placeholder="Search for a work operation"
                        suggestions={this.getSuggestions()}
                        filterSuggestions={this.filterSuggestions}
                        getSuggestionValue={this.getSuggestionValue}
                        renderSuggestionValue={this.renderSuggestionValue}
                        renderSectionTitle={this.renderSectionTitle}
                        getSectionSuggestions={this.getSectionSuggestions}
                        onSelect={this.handleWorkSelected}
                        onReset={this.handleReset}
                      />
                    </LegacyForm.Item>
                  </LegacyForm>
                </div>
              )}
              {showWorkDescription && !this.isNew && !sku && (
                <div>
                  <LegacyForm>
                    <h3>Edit work operation</h3>
                    <Input
                      defaultValue="Other"
                      disabled
                      style={{ width: '100%', height: 56 }}
                    />
                  </LegacyForm>
                </div>
              )}
              {showWorkDescription && (
                <div>
                  <LegacyForm>
                    <h3>{!this.isNew && sku ? 'Edit work operation' : 'Work description'}</h3>
                    <LegacyForm.Item
                      validateStatus={validateStatusWorkDescription}
                      help={validateStatusWorkDescription === 'error' ? 'Please name the work operation' : ''}
                    >
                      <Input
                        onChange={this.handleWorkDescriptionChange}
                        onBlur={this.handleWorkDescriptionOnBlur}
                        defaultValue={workSummary}
                        disabled={!this.isNew && sku}
                        style={{ width: '100%', height: 56 }}
                        {...this.fieldOptions.workDescription}
                      />
                    </LegacyForm.Item>
                  </LegacyForm>
                </div>
              )}
            </div>
            {workSummary && (
              <div className="criticality">
                <h3>Criticality</h3>
                <Radio.Group
                  onChange={this.handleUrgencyChange}
                  defaultValue={urgency}
                  disabled={get(this.props.fieldOptions, 'urgency.disabled', false)}
                >
                  {this.fieldOptions.urgency.map(([label, val]) => (
                    <RBContainer key={val.name} styles={val.style}>
                      <Popover
                        placement="bottom"
                        content={(
                          <div>
                            <p>{val.description}</p>
                          </div>
                        )}
                      ><Radio.Button style={urgency === label ? { border: '1px solid rgba(0, 0, 0, 0.25)' } : null} value={val.name}>{val.name}</Radio.Button>
                      </Popover>
                    </RBContainer>
                  ))}
                </Radio.Group>
              </div>
            )}
            {(!isEmpty(workSummary) && urgency) && (
              <Fragment>
                <div>
                  <WorkOperationsTableComp
                    ref={this.refWorkOperationTable}
                    columns={this.columns}
                    dataSource={this.dataSource}
                    onSetEditRow={this.onSetEditRow}
                    validator={validator}
                    defaultExpandAllRows
                    expandRowByClick={false}
                    expandedRowRender={undefined}
                    expandIcon={() => null}
                  />
                  {this.props.renderUnderTable}
                </div>
                <CommentContainer>
                  {this.fieldOptions.commentForCM.display && (
                    <div className="comment">
                      <LegacyForm>
                        <h3>Comment for Fixter</h3>
                        <LegacyForm.Item
                          validateStatus={validateStatusCmComment}
                          help={validateStatusCmComment === 'error' ? 'Please help us sell the work operation to the customer' : ''}
                        >
                          <TextArea
                            value={commentForCM}
                            onBlur={this.handleCMCommentOnBlur}
                            placeholder={this.commentPlaceholder()}
                            onChange={this.handleCMCommentChange}
                            rows={4}
                            {...omit(this.fieldOptions.commentForCM, ['display'])}
                          />
                        </LegacyForm.Item>
                      </LegacyForm>
                    </div>
                  )}
                  {this.fieldOptions.commentForCustomer.display && (
                    <div className="comment">
                      <LegacyForm>
                        <h3>Comment for Customer</h3>
                        <LegacyForm.Item
                          validateStatus={validateStatusCustomerComment}
                          help={validateStatusCustomerComment === 'error' ? 'Please explain why the work operation need to be carried out.' : ''}
                        >
                          <TextArea
                            value={commentForCustomer}
                            onBlur={this.handleCustomerCommentOnBlur}
                            placeholder={this.commentForCustomerPlaceholder()}
                            onChange={this.handleCustomerCommentChange}
                            rows={4}
                            {...omit(this.fieldOptions.commentForCustomer, ['display'])}
                          />
                        </LegacyForm.Item>
                      </LegacyForm>
                    </div>
                  )}
                </CommentContainer>
              </Fragment>
            )}
            {urgency && (
              <ActionButtons
                isNew={this.isNew}
                saveDisabled={this.isSaveButtonDisabled()}
                showDelete={this.props.showDelete}
                onDelete={this.handleDelete}
                onCancel={this.props.onClose}
                onSave={this.handleSave}
                okText={this.props.modalOkText || (this.isNew ? 'Add' : 'Save')}
              />
            )}
          </Fragment>
        </Wrapper>
      </Modal>
    );
  }
}

WorkOperationEditor.propTypes = {
  columns: PropTypes.array.isRequired,
  fieldOptions: PropTypes.shape({
    urgency: PropTypes.shape({
      list: PropTypes.object,
      onChange: PropTypes.func,
    }),
    workAutocomplete: PropTypes.shape({
      onSelect: PropTypes.func,
      filterOption: PropTypes.func,
    }),
    workDescription: PropTypes.shape({
      onChange: PropTypes.func,
    }),
    commentForCM: PropTypes.shape({
      display: PropTypes.bool,
      disabled: PropTypes.bool,
    }),
    commentForCustomer: PropTypes.shape({
      display: PropTypes.bool,
      disabled: PropTypes.bool,
    }),
  }),
  /** Used to create an empty line item */
  defaultLineItemData: PropTypes.object.isRequired,
  /** Specify which columns from work operation will display in total row */
  fixedTotalColumns: PropTypes.array,
  /** Specify which columns will display automatically calculated totals */
  calculateTotalColumns: PropTypes.array.isRequired,
  /** work operation to edit */
  workOperation: PropTypes.object,
  displayQuoteEnginetooltip: PropTypes.bool,
  autocompleteList: PropTypes.array,
  saveDisabled: PropTypes.bool,
  showDelete: PropTypes.bool,
  modalOkText: PropTypes.string,
  editTotalRow: PropTypes.bool,
  renderUnderTable: PropTypes.object,
  validator: PropTypes.func,
  onClose: PropTypes.func,
  onDelete: PropTypes.func,
  onSave: PropTypes.func,
  onSetEditRow: PropTypes.func,
  onLineItemDelete: PropTypes.func,
  isCommentMandatory: PropTypes.bool,
  hideAddPartRow: PropTypes.bool,
};

WorkOperationEditor.defaultProps = {
  workOperation: {},
  fixedTotalColumns: [],
  autocompleteList: [],
  saveDisabled: true,
  showDelete: true,
  displayQuoteEnginetooltip: false,
  editTotalRow: false,
  renderUnderTable: null,
  validator: () => {},
  onClose: () => {},
  onDelete: () => {},
  onSave: () => {},
  onSetEditRow: () => {},
  onLineItemDelete: () => {},
  fieldOptions: {
    urgency: {
      list: [],
      onChange: () => {},
    },
    workAutocomplete: {
      onSelect: () => {},
      filterOption: () => {},
    },
    workDescription: {
      onChange: () => {},
    },
    commentForCM: {
      display: true,
      disabled: false,
    },
    commentForCustomer: {
      display: false,
      disabled: false,
    },
  },
  modalOkText: null,
  isCommentMandatory: false,
  hideAddPartRow: false,
};
